// src/prototype/components/help/HelpPopover.tsx
// ============================================================
// HELP POPOVER COMPONENT
//
// A lightweight, reusable contextual help component that displays
// a small icon button which opens a popover with help content.
//
// Features:
// - Click or keyboard activation (Enter/Space)
// - Outside click to close
// - Escape key to close
// - Accessible with aria-label and focus management
// - DVA brand styling (navy text, subtle border, rounded shadow)
// - Portal rendering to avoid overflow clipping in nested layouts
//
// Usage:
//   <HelpPopover helpKey="personalization_strength" />
//   <HelpPopover helpKey="roi_model" className="ml-1" placement="top" />
// ============================================================

import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle } from 'lucide-react';
import { helpContent, HelpKey } from '../../help/helpContent';

const POPOVER_WIDTH = 256;

interface HelpPopoverProps {
  /** The help content key to display */
  helpKey: HelpKey;
  /** Optional additional CSS classes for the wrapper */
  className?: string;
  /** Icon size in pixels (default: 14) */
  iconSize?: number;
  /** Popover placement relative to the trigger (default: bottom) */
  placement?: 'top' | 'bottom';
  /** Optional additional CSS classes for the trigger button */
  buttonClassName?: string;
}

interface PopoverCoords {
  top: number;
  left: number;
}

function clampPopoverLeft(centerX: number): number {
  const halfWidth = POPOVER_WIDTH / 2;
  return Math.min(
    Math.max(halfWidth + 16, centerX),
    window.innerWidth - halfWidth - 16
  );
}

export function HelpPopover({
  helpKey,
  className = '',
  iconSize = 14,
  placement = 'bottom',
  buttonClassName = '',
}: HelpPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<PopoverCoords | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const content = helpContent[helpKey];

  const updateCoords = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    setCoords({
      top: placement === 'bottom' ? rect.bottom + 8 : rect.top,
      left: clampPopoverLeft(rect.left + rect.width / 2),
    });
  }, [placement]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setCoords(null);
      return;
    }

    updateCoords();
    window.addEventListener('scroll', updateCoords, true);
    window.addEventListener('resize', updateCoords);
    return () => {
      window.removeEventListener('scroll', updateCoords, true);
      window.removeEventListener('resize', updateCoords);
    };
  }, [isOpen, updateCoords]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleToggle = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setIsOpen(prev => !prev);
  }, []);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      handleToggle(event as unknown as React.MouseEvent);
    }
  }, [handleToggle]);

  if (!content) {
    console.warn(`[HelpPopover] Unknown helpKey: ${helpKey}`);
    return null;
  }

  const popover =
    isOpen && coords
      ? createPortal(
          <div
            ref={popoverRef}
            role="tooltip"
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              transform:
                placement === 'top'
                  ? 'translate(-50%, calc(-100% - 8px))'
                  : 'translateX(-50%)',
              zIndex: 9999,
            }}
            className="w-64 max-w-[calc(100vw-2rem)] bg-white rounded-lg border border-[#e5e7eb] shadow-lg help-popover-animate"
          >
            <div
              className={`absolute left-1/2 -translate-x-1/2 w-3 h-3 bg-white rotate-45 ${
                placement === 'top'
                  ? '-bottom-[6px] border-r border-b border-[#e5e7eb]'
                  : '-top-[6px] border-l border-t border-[#e5e7eb]'
              }`}
            />

            <div className="relative p-3">
              <p className="text-xs font-semibold text-[#18365E] mb-1">{content.title}</p>
              <p className="text-[11px] text-[#717182] leading-relaxed">{content.body}</p>
              {content.learnMoreHref && (
                <a
                  href={content.learnMoreHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-2 text-[10px] font-medium text-[#FF6E4C] hover:text-[#FF6E4C]/80 transition-colors"
                >
                  Learn more →
                </a>
              )}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <span
      className={`relative inline-flex items-center ${className}`}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button
        ref={buttonRef}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        aria-label={`Help: ${content.title}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={`
          inline-flex items-center justify-center
          w-[18px] h-[18px] rounded-full
          text-[#717182] hover:text-[#18365E]
          hover:bg-[#18365E]/5
          focus:outline-none focus:ring-2 focus:ring-[#FF6E4C]/30 focus:ring-offset-1
          transition-colors cursor-pointer
          ${isOpen ? 'text-[#18365E] bg-[#18365E]/10' : ''}
          ${buttonClassName}
        `}
      >
        <HelpCircle style={{ width: iconSize, height: iconSize }} />
      </button>

      {popover}

      <style>{`
        @keyframes help-popover-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .help-popover-animate {
          animation: help-popover-fade-in 150ms ease-out;
        }
      `}</style>
    </span>
  );
}

export default HelpPopover;
